import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

import { resourceTypeToResourcePathSlug } from "@/components/Schedule/useScheduleResource";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { ShortcutBadge } from "@/Utils/keyboardShortcutComponents";
import { formatPatientAge } from "@/Utils/utils";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { FacilityRead } from "@/types/facility/facility";
import {
  formatScheduleResourceName,
  SchedulableResourceType,
} from "@/types/scheduling/schedule";
import { renderTokenNumber, TokenRetrieve } from "@/types/tokens/token/token";
import { PrinterIcon } from "lucide-react";
import { Link } from "raviger";

import PrintEmailDialog from "@/CAREUI/misc/PrintEmailDialog";

interface Props {
  id?: string;
  token: TokenRetrieve;
  facility: FacilityRead;
  className?: string;
  tokenActions?: boolean;
  showlogo?: boolean;
}

const TokenCard = ({
  id,
  token,
  facility,
  className,
  tokenActions = true,
  showlogo = true,
}: Props) => {
  const { t } = useTranslation();

  const printToken = (tokenId: string) => {
    const printSection = document.getElementById(`print-token-${tokenId}`);

    if (printSection) {
      const style = document.createElement("style");
      style.textContent = `
        @media print {
          body * { visibility: hidden; }
          #print-token-${tokenId}, #print-token-${tokenId} * { visibility: visible; }
          #print-token-${tokenId} {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
          }
        }
      `;
      document.head.appendChild(style);
      window.print();
      document.head.removeChild(style);
    }
  };

  return (
    <Card
      id={id}
      className={cn(
        "p-3 border border-gray-100 print:border-gray-200 relative hover:scale-101 hover:shadow-md transition-all duration-300 ease-in-out print:scale-100 print:rotate-0 print:shadow-none print:hover:scale-100 print:hover:rotate-0 print:hover:shadow-none",
        className,
      )}
    >
      {showlogo && (
        <div className="absolute inset-0 opacity-[0.1] pointer-events-none bg-[url('/images/care_logo_gray.svg')] bg-center bg-no-repeat bg-[length:40%_auto] lg:bg-[length:60%_auto]" />
      )}

      <div className="relative z-10">
        <div className="flex flex-col gap-2">
          <div className="flex w-full gap-2">
            <div className="flex flex-col w-full gap-2">
              <div className="flex-shrink-0 w-fit -mt-3">
                <div className="px-2 py-2 text-sm font-medium text-center text-gray-500 bg-gray-100 border whitespace-nowrap rounded-b-md">
                  <p>{token.category.name}</p>
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-600">
                {token.queue.date}
              </p>
              {token.patient && (
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-normal text-gray-600">
                    {t("patient_name")}:
                  </Label>
                  <p className="font-semibold break-words">
                    {token.patient.name}
                  </p>
                  <p className="text-sm font-medium text-gray-700">
                    {`${formatPatientAge(token.patient, true)}, ${t(`GENDER__${token.patient.gender}`)}`}
                  </p>
                </div>
              )}
              <div>
                <Label className="text-sm font-normal text-gray-600">
                  {t(`schedulable_resource__${token.resource_type}`)}:
                </Label>
                <p className="text-sm font-semibold break-words">
                  {formatScheduleResourceName(token)}
                </p>
                {token.resource_type === SchedulableResourceType.Location &&
                  token.resource.description && (
                    <p className="text-xs text-gray-600 break-words">
                      {token.resource.description}
                    </p>
                  )}
                {token.resource_type ===
                  SchedulableResourceType.HealthcareService &&
                  token.resource.extra_details && (
                    <p className="text-xs text-gray-600 break-words">
                      {token.resource.extra_details}
                    </p>
                  )}
              </div>
            </div>
            <div className="flex flex-col items-end w-full gap-2">
              <div>
                <Label className="justify-end text-sm font-normal text-gray-600">
                  {t("token_no")}
                </Label>
                <div className="flex justify-end text-xl xl:text-2xl font-bold leading-none">
                  {renderTokenNumber(token)}
                </div>
              </div>
              <QRCodeSVG
                size={96}
                value={JSON.stringify({
                  tokenId: token.id,
                  queueId: token.queue.id,
                })}
              />
            </div>
          </div>
          <Separator />
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold tracking-tight">
              {facility.name}
            </div>
            <div className="text-sm text-gray-600 whitespace-pre-wrap wrap-break-word">
              <span>{facility.address?.replace(/,\s*/g, ", ")}</span>
              <div className="whitespace-normal">{`Ph.: ${facility.phone_number}`}</div>
            </div>
          </div>
        </div>
        {tokenActions && (
          <div>
            <Separator className="my-3 print:hidden" />
            <div className="flex items-center justify-between print:hidden">
              <Button
                variant="link"
                asChild
                className="text-base font-semibold underline capitalize text-gray-950"
              >
                <Link
                  href={`/facility/${facility.id}/${resourceTypeToResourcePathSlug[token.resource_type]}/${token.resource.id}/queues/${token.queue.id}`}
                >
                  {t("queue_board")}
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={() => printToken(token.id)}
                className="text-base font-semibold text-gray-950"
              >
                <PrinterIcon className="mr-2 size-4" />
                {t("print")}
                <ShortcutBadge actionId="print-button" />
              </Button>
              <PrintEmailDialog
                documentTitle={t("token")}
                patientId={token.patient?.id}
                patientEmail={token.patient?.email}
                patientName={token.patient?.name}
                printableElementId={`print-token-${token.id}`}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export { TokenCard };
